import { Product } from '@/types/ecommerce.types';
import { applyDiscountToPrice } from '@/utils/applyDiscountToPrice';
import { getCurrentVariant } from '@/utils/utils';
import { ProductVariantEdge } from '@shopify/hydrogen-react/storefront-api-types';

export type PriceParams = {
  product?: Product;
  newPrice?: string;
  variant?: ProductVariantEdge;
  isGallery?: boolean;
  itemsLength?: number;
};

export type PriceBreakdown = {
  price: string;
  discountPrice: string;
  comparePrice?: string;
  hasPromotionalDiscount: boolean;
  isValid: boolean;
};

const shouldApplyPromotionalDiscount = (
  originalPrice: string,
  discountedPrice: string,
  isGallery: boolean,
  itemsLength: number
) => {
  if (originalPrice === discountedPrice) return false;
  if (!isGallery && window.cartQty === 1 && window.cartItemTotal > window.cartQty) return false;

  return window.sitewideDiscount || window.cartItemTotal > 0 || (isGallery && itemsLength > 1);
};

const parsePrice = (value: string | number | null | undefined): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return isNaN(parsed) ? null : parsed;
};

export const formatPrice = (value: string | number): string => {
  const amount = parsePrice(value);
  if (amount === null) return '';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })
    .format(amount)
    .replace(/(\.|,)00$/g, '');
};

export const formatRoundedPrice = (amount: string | number): string => {
  const parsed = parsePrice(amount);
  if (parsed === null) return '';

  return formatPrice(Math.ceil(parsed));
};

const getVariantPricing = (params: PriceParams) => {
  const { product, newPrice, variant } = params;
  const currentVariant = variant?.node ?? getCurrentVariant(product?.variants?.edges);

  return {
    basePrice: newPrice ?? currentVariant?.price?.amount,
    compareAtPrice: currentVariant?.compareAtPrice?.amount,
  };
};

export const calculatePrices = (params: PriceParams): PriceBreakdown => {
  const { isGallery = false, itemsLength = 0 } = params;
  const { basePrice, compareAtPrice } = getVariantPricing(params);

  if (parsePrice(basePrice) === null) {
    return {
      price: '',
      discountPrice: '',
      comparePrice: undefined,
      hasPromotionalDiscount: false,
      isValid: false,
    };
  }

  const discountedAmount = applyDiscountToPrice(basePrice!, isGallery ? itemsLength : 0);
  const formattedPrice = formatRoundedPrice(basePrice!);
  const formattedDiscountPrice = formatRoundedPrice(discountedAmount);
  const formattedComparePrice = compareAtPrice ? formatRoundedPrice(compareAtPrice) : undefined;

  const hasPromotionalDiscount = shouldApplyPromotionalDiscount(
    formattedPrice,
    formattedDiscountPrice,
    isGallery,
    itemsLength
  );

  return {
    price: formattedPrice,
    discountPrice: formattedDiscountPrice,
    comparePrice: formattedComparePrice,
    hasPromotionalDiscount,
    isValid: parsePrice(discountedAmount) !== null,
  };
};
